import {
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';
import { showServerError } from '@/shared/lib/toast-helpers';
import { track } from '@/shared/lib/analytics';
import { useAuthStore, SessionIdleExpiredError } from '@/auth/auth-store';
import { ActiveSessionConflictError } from '@/auth/auth-api';
import { ApiError } from '@/shared/api/api-client';

/**
 * Is this failure a session-lifecycle event rather than a real server error?
 * Idle-timeout, a bubbled 401 (refresh already failed), and an active-session
 * conflict are all communicated by their own UI — the session-expired modal,
 * the /login redirect, or the "Ongoing Session" dialog. Firing the generic
 * "Something went wrong" toast on top of those is redundant noise (it's what
 * showed behind the session-expired modal during the idle demo).
 *
 * Gated strictly on the specific session-end error TYPES — NOT broadly on the
 * `sessionExpired` flag (CoS review PR #670). A blanket "suppress everything
 * while sessionExpired" could silently swallow a genuine, unrelated failure
 * that coincides with the expiry window. When a session ends, every failing
 * query throws one of these types anyway (`getValidAccessToken` →
 * SessionIdleExpiredError once `sessionExpired` is set, or a bubbled 401), so
 * type-gating loses no coverage on the real cascade.
 */
function isSessionEndError(error: unknown): boolean {
  if (error instanceof SessionIdleExpiredError) return true;
  if (error instanceof ActiveSessionConflictError) return true;
  if (error instanceof ApiError && error.status === 401) return true;
  return false;
}

/**
 * Mixpanel "Error Occurred" event on API failures — Flutter parity.
 *
 * Flutter `dio_api_client.dart:248-286` fires `Error Occurred` on every
 * NON-401 API error. We replicate it (the event already exists in the live
 * Scrips Practice Management Mixpanel project), with one **deliberate HIPAA
 * deviation**: Flutter also logs the full request + response BODIES + the
 * user's display name to Mixpanel, which would leak PHI. We log ONLY
 * non-identifying metadata — the logical context key (queryKey[0] /
 * mutationKey[0], never a value), the HTTP status, and the org id.
 *
 * NEVER add the ApiError `.message` here: it is `API Error {status}: {body}`,
 * so it embeds the response body (PHI). Same for request bodies and
 * query-param values — keys only, per `analytics.ts`'s PHI rule.
 *
 * Skips 401 (Flutter skips it too — it's the refresh/expiry path, handled in
 * api-client + the session-expired modal, not an "error" to log).
 */
export function trackApiError(context: string, error: unknown): void {
  const status = error instanceof ApiError ? error.status : null;
  if (status === 401) return;
  track('Error Occurred', {
    context,
    status,
    error_kind: error instanceof ApiError ? 'http' : 'network',
    organization_id: useAuthStore.getState().user?.organizationId ?? null,
  });
}

/**
 * Single application-wide QueryClient.
 *
 * Mounted at the root in `main.tsx`; every domain (admin, scheduling,
 * billing, clinical, …) shares this instance so the global error
 * handler catches *every* failure in the app.
 *
 * Global error handling — every failed query and mutation surfaces the
 * generic Scrips support message via `showServerError`. Per-call
 * `onError` handlers can still run for component-local cleanup, but
 * they should NOT call `showServerError` again or the user double-toasts.
 *
 * Per-call opt-out: `meta: { skipGlobalError: true }` on the query or
 * mutation options.
 *
 * Narrower opt-out for a mutation whose failure the screen already shows
 * inline: `meta: { handledInline: true }`. It drops only the toast; the
 * analytics event still fires.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Match Flutter's "fetch once, refetch only on user action or mutation
      // invalidation" semantics. By default React Query refetches when the
      // window regains focus and on network reconnect, which causes
      // surprise GETs every time the user tabs back from devtools — see
      // billing_module's `service_list_widget.dart:50`, which only fires
      // FetchServiceListEvent in `initState` plus user-driven events.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Long stale window so the cache stays fresh across navigations
      // within a session. Mutations invalidate explicitly when data changes.
      staleTime: 5 * 60_000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // skipGlobalError suppresses BOTH the toast and the analytics event —
      // an intentionally-silent fetch (e.g. org-features) shouldn't surface a
      // toast or pollute the error funnel.
      if (query.meta?.skipGlobalError) return;
      // Session ended (idle / expired / conflict) — the modal or redirect
      // owns the messaging; don't stack a "Something went wrong" toast.
      if (isSessionEndError(error)) return;
      const ctx = `query:${String(query.queryKey[0] ?? 'unknown')}`;
      trackApiError(ctx, error);
      showServerError(ctx, error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.skipGlobalError) return;
      if (isSessionEndError(error)) return;
      const key = mutation.options.mutationKey?.[0];
      const ctx = `mutation:${String(key ?? 'unknown')}`;
      trackApiError(ctx, error);
      // The caller renders this failure inline; a toast on top would cover it.
      if (mutation.meta?.handledInline) return;
      showServerError(ctx, error);
    },
  }),
});
